import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { HelpCircle, MessageCircle } from 'lucide-react';

const GAPFAQSection = () => {
  return (
    <section id="gap-faq" className="py-20 lg:py-28 bg-background">
      <div className="section-container">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Column - Header */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
                Got Questions?
              </span>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                GAP Insurance FAQs
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Find answers to common questions about GAP insurance mis-selling claims. 
                Cannot find what you are looking for? Get in touch with our team.
              </p>
              
              {/* Contact Card */}
              <div className="p-6 rounded-2xl bg-muted/50 border border-border">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">
                      Still have questions?
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Speak to our team for personalised advice
                    </p>
                  </div>
                </div>
                <Button variant="outline" className="w-full" asChild>
                  <a href="/contact">Contact Us</a>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column - FAQ Accordion */}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem
                value="gap-item-1"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>What is GAP insurance mis-selling?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  GAP (Guaranteed Asset Protection) insurance was widely mis-sold by car dealerships who failed to disclose the enormous commissions they were earning. The FCA found that dealers were keeping up to 70% of GAP insurance premiums as hidden commission, while only around 6% of premiums were ever paid out in claims. This meant consumers were paying vastly inflated prices for policies that often provided poor value. If you were not told about the commission the dealer earned, or were pressured into buying GAP insurance at the point of sale without being told you could shop around for a better deal, your policy may have been mis-sold.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="gap-item-2"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How do I know if my GAP insurance was mis-sold?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Your GAP insurance may have been mis-sold if any of the following apply: you were not told that the dealer was earning a commission from selling you the policy; you were pressured or rushed into buying GAP insurance at the dealership without time to consider alternatives; you were not informed that you could purchase GAP insurance elsewhere, often at a significantly lower price; the policy was not suitable for your circumstances, for example if you had no outstanding finance; or the terms, exclusions and limitations of the policy were not properly explained to you. Following the FCA's market study, 80% of GAP insurance providers suspended sales, which demonstrates how widespread the problems were.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="gap-item-3"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>What compensation could I receive for mis-sold GAP insurance?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  If your GAP insurance was mis-sold, you could be entitled to a full refund of all premiums you paid for the policy, plus interest on those payments. The exact amount depends on how much you paid for your GAP insurance and how long ago you purchased it. Many consumers paid around £375 on average for GAP insurance at the dealership — the figure recorded in the FCA's own data — when equivalent cover was available online for a fraction of the price. If you also had car finance arranged through the same dealer, you may be able to claim for both GAP insurance mis-selling and PCP or HP commission mis-selling, potentially increasing your total compensation significantly.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="gap-item-4"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Can I claim for both GAP insurance and car finance mis-selling?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Yes, absolutely. Many drivers who purchased a car through a dealership were affected by both scandals at the same time. If you arranged PCP or HP finance and also purchased GAP insurance from the dealer, you may have two separate claims: one for the hidden commission on your car finance, and another for the mis-sold GAP insurance policy. These are assessed independently, meaning you could receive compensation for both. Blue Lion Law can check your eligibility for both types of claim at the same time, ensuring you do not miss out on any compensation you may be owed.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="gap-item-5"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Do I need my GAP insurance documents to make a claim?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  No, you do not need to have kept your original GAP insurance documents. Our system can trace your finance agreements and associated insurance products going back to 2008 using just your name, date of birth and current address. Many people do not remember which company provided their GAP insurance or how much they paid, and that is not a problem. We have the tools to locate this information on your behalf. All you need to do is start the process and we handle the rest.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="gap-item-6"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How long does a GAP insurance claim take?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  The timeline for GAP insurance claims varies depending on the insurance provider and the complexity of your case. Once you submit your details, we will identify your GAP insurance policy, assess whether it was mis-sold, and submit your claim to the provider. Providers are required to respond within eight weeks. If they reject your claim or you are unhappy with their response, we can escalate the matter to the Financial Ombudsman Service on your behalf. Throughout the process, we keep you informed of progress so you always know where your claim stands.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export { GAPFAQSection };
